import { useState, useEffect } from 'react';
import { Shield, AlertTriangle, TrendingDown, DollarSign, Calendar, ArrowUp, ArrowDown, Lock, TrendingUp, Lightbulb, Target, Users, Wallet, Zap } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useSubscriptionAccess } from '../hooks/useSubscriptionAccess';
import { UpgradeModal } from './UpgradeModal';
import { computeIndicators, saveIndicatorsSnapshot, IndicatorsResult } from '../utils/indicatorsEngine';
import { CrisisScenarioManager } from './crisis/CrisisScenarioManager';

export function SecurityTab() {
  const { effectiveUserId } = useAuth();
  const { hasActiveSubscription, planTier } = useSubscriptionAccess();
  const [showCrisisScenarios, setShowCrisisScenarios] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [indicators, setIndicators] = useState<IndicatorsResult | null>(null);
  const [stressTests, setStressTests] = useState<any[]>([]);
  const [selectedTest, setSelectedTest] = useState<string | null>(null);

  useEffect(() => {
    if (hasActiveSubscription && effectiveUserId) {
      loadStressTests();
      loadLatestIndicators();
    }
  }, [hasActiveSubscription, effectiveUserId]);

  const loadStressTests = async () => {
    if (!effectiveUserId) return;

    const { data } = await supabase
      .from('stress_tests')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('created_at', { ascending: false })
      .limit(10);

    if (data) {
      setStressTests(data);
    }
  };

  const loadLatestIndicators = async () => {
    if (!effectiveUserId) return;

    const { data } = await supabase
      .from('indicators_snapshots')
      .select('*')
      .eq('user_id', effectiveUserId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    if (data) {
      setIndicators(data);
    }
  };

  const runStressTest = async () => {
    if (!effectiveUserId || !hasActiveSubscription) return;

    setLoading(true);
    try {
      const result = await computeIndicators(effectiveUserId, null, {
        start_date: new Date(),
        horizon_days: 180,
        compressible_reduction_pct: 0,
      });

      const { data: stressTest } = await supabase
        .from('stress_tests')
        .insert({
          user_id: effectiveUserId,
          test_type: 'zero_income',
          test_name: 'Stress Test Zéro Encaissement',
          start_date: new Date().toISOString().split('T')[0],
          horizon_days: 180,
        })
        .select()
        .single();

      if (stressTest) {
        await saveIndicatorsSnapshot(effectiveUserId, result, null, stressTest.id);
        setIndicators(result);
        loadStressTests();
      }
    } catch (error) {
      console.error('Error running stress test:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!hasActiveSubscription) {
    return (
      <>
        <div className="flex flex-col items-center justify-center h-full py-12 px-4">
          <div className="bg-white rounded-lg shadow-lg p-8 max-w-2xl w-full text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock size={32} className="text-blue-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Fonctionnalité Sécurité
            </h2>
            <p className="text-gray-600 mb-6">
              Les stress tests et scénarios de crise nécessitent un abonnement actif
              pour protéger votre trésorerie contre les imprévus.
            </p>
            <button
              onClick={() => setShowUpgradeModal(true)}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Voir les offres
            </button>
          </div>
        </div>

        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          currentPlan={planTier}
          targetPlan="vision"
          blockedFeature="stress_tests"
        />
      </>
    );
  }

  if (showCrisisScenarios) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <button
          onClick={() => setShowCrisisScenarios(false)}
          className="mb-6 flex items-center space-x-2 text-gray-600 hover:text-gray-900"
        >
          <ArrowDown className="h-5 w-5" />
          <span>Retour aux stress tests classiques</span>
        </button>
        <CrisisScenarioManager />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <Shield size={32} className="text-blue-600" />
          Sécurité & Stress Tests
        </h1>
        <p className="text-gray-600 mt-2">
          Testez la résistance de votre trésorerie et obtenez des recommandations actionnables
        </p>
      </div>

      <div className="mb-6 flex items-center gap-4">
        <button
          onClick={runStressTest}
          disabled={loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Calcul en cours...' : 'Lancer un Stress Test'}
        </button>
        <button
          onClick={() => setShowCrisisScenarios(true)}
          className="flex items-center gap-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Zap size={20} />
          <span>Créer un Crash Test</span>
        </button>
      </div>

      {indicators && (
        <>
          {indicators.burn_rate_monthly === 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-blue-800">
                <strong>Note :</strong> Aucune dépense n'a été trouvée dans votre plan de trésorerie, le calcul du burn rate et du temps de survie n'est pas possible.
                Créez un plan de trésorerie dans l'onglet "Trésorerie" ou ajoutez des transactions historiques pour obtenir des indicateurs.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-2 mb-2">
                <Calendar size={20} className="text-blue-600" />
                <h3 className="font-semibold text-gray-900">Temps de Survie Financière</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {indicators.tsf_months > 100 ? '∞' : `${indicators.tsf_months.toFixed(1)} mois`}
              </p>
              {indicators.tsf_months_recurring !== indicators.tsf_months && indicators.tsf_months_recurring < 100 && (
                <p className="text-sm font-medium text-green-600 mt-1">
                  {indicators.tsf_months_recurring.toFixed(1)} mois (récurrent)
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Durée pendant laquelle votre trésorerie peut couvrir les dépenses sans nouveaux encaissements
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-2 mb-2">
                <TrendingDown size={20} className="text-orange-600" />
                <h3 className="font-semibold text-gray-900">Burn Rate</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {indicators.burn_rate_monthly.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
              </p>
              {indicators.burn_rate_monthly_recurring !== indicators.burn_rate_monthly && (
                <p className="text-sm font-medium text-green-600 mt-1">
                  {indicators.burn_rate_monthly_recurring.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€ (récurrent)
                </p>
              )}
              <p className="text-xs text-gray-500 mt-2">
                Moyenne mensuelle des dépenses prévues au plan de trésorerie
              </p>
            </div>

            <div className="bg-white p-6 rounded-lg shadow">
              <div className="flex items-center gap-2 mb-2">
                <Shield size={20} className="text-green-600" />
                <h3 className="font-semibold text-gray-900">FRD Requis</h3>
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {indicators.frd_required.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Montant à ajouter pour que le plan de trésorerie reste toujours positif
              </p>
            </div>

            <div className={`bg-white p-6 rounded-lg shadow ${
              indicators.safety_gap >= 0 ? 'border-l-4 border-green-500' : 'border-l-4 border-red-500'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                {indicators.safety_gap >= 0 ? (
                  <ArrowUp size={20} className="text-green-600" />
                ) : (
                  <ArrowDown size={20} className="text-red-600" />
                )}
                <h3 className="font-semibold text-gray-900">Écart de Sécurité</h3>
              </div>
              <p className={`text-3xl font-bold ${
                indicators.safety_gap >= 0 ? 'text-green-600' : 'text-red-600'
              }`}>
                {indicators.safety_gap.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
              </p>
              <p className="text-xs text-gray-500 mt-2">
                Différence entre votre trésorerie actuelle et le FRD requis
              </p>
            </div>
          </div>

          {indicators.exceptional_expenses && indicators.exceptional_expenses.length > 0 && (
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-orange-900 mb-3 flex items-center gap-2">
                <AlertTriangle size={20} className="text-orange-600" />
                Dépenses exceptionnelles détectées
              </h3>
              <div className="space-y-3 text-orange-900">
                <p className="text-sm leading-relaxed">
                  Les dépenses suivantes ont été identifiées comme exceptionnelles (seuil: 10 000€) et sont exclues du calcul du burn rate récurrent :
                </p>
                <div className="bg-white p-4 rounded-lg space-y-2">
                  {indicators.exceptional_expenses.map((expense, idx) => (
                    <div key={idx} className="flex justify-between items-center border-b border-gray-200 pb-2 last:border-0 last:pb-0">
                      <div>
                        <p className="font-medium text-gray-900">{expense.label}</p>
                        <p className="text-xs text-gray-500">{new Date(expense.date).toLocaleDateString('fr-FR')}</p>
                      </div>
                      <p className="font-bold text-red-600">
                        {expense.amount.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                      </p>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-orange-800 bg-orange-100 p-3 rounded">
                  Ces dépenses exceptionnelles représentent un total de{' '}
                  <strong>
                    {indicators.exceptional_expenses.reduce((sum, e) => sum + Math.abs(e.amount), 0).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                  </strong>.
                  Le burn rate récurrent ({indicators.burn_rate_monthly_recurring.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€/mois)
                  reflète mieux vos charges mensuelles normales.
                </p>
              </div>
            </div>
          )}

          {indicators.opening_cash_difference && Math.abs(indicators.opening_cash_difference) > 1000 && (
            <div className="bg-yellow-50 border-l-4 border-yellow-500 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
                <AlertTriangle size={20} className="text-yellow-600" />
                Réconciliation de trésorerie
              </h3>
              <div className="space-y-3 text-yellow-900">
                <p className="text-sm leading-relaxed">
                  Une différence a été détectée entre vos comptes bancaires et l'historique des transactions :
                </p>
                <div className="bg-white p-4 rounded-lg space-y-2">
                  <p>
                    <strong>Selon les comptes bancaires :</strong>{' '}
                    {indicators.opening_cash_from_accounts?.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                  </p>
                  <p>
                    <strong>Selon l'historique des transactions :</strong>{' '}
                    {(indicators.opening_cash_from_accounts - indicators.opening_cash_difference).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                  </p>
                  <p className="font-bold text-yellow-800">
                    <strong>Écart :</strong>{' '}
                    {indicators.opening_cash_difference.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                  </p>
                </div>
                <p className="text-sm text-yellow-800 bg-yellow-100 p-3 rounded">
                  Cet écart peut être normal si vous n'avez pas importé tout l'historique de vos transactions.
                  Les calculs utilisent les soldes bancaires actuels comme source de vérité.
                </p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border-l-4 border-blue-500 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
              <Shield size={20} className="text-blue-600" />
              Fonds de Roulement Dynamique (FRD)
            </h3>
            <div className="space-y-3 text-blue-900">
              <p className="text-sm leading-relaxed">
                Le FRD est le montant de trésorerie initiale minimale à ajouter à votre plan de trésorerie
                pour que le solde reste toujours positif sur la période considérée.
              </p>
              <div className="bg-white p-4 rounded-lg space-y-2">
                <p>
                  <strong>Trésorerie actuelle :</strong>{' '}
                  {indicators.opening_cash.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
                <p>
                  <strong>FRD requis :</strong>{' '}
                  {indicators.frd_required.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
                <p>
                  <strong>Trésorerie cible :</strong>{' '}
                  {(indicators.opening_cash + indicators.frd_required).toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
                <p>
                  <strong>Zone de sécurité actuelle :</strong>{' '}
                  {indicators.frd_safety_days} jours
                </p>
              </div>
              {indicators.frd_required > 0 && (
                <p className="text-sm mt-3 text-blue-800 bg-blue-100 p-3 rounded">
                  Pour que votre plan de trésorerie reste toujours positif, il vous faut ajouter{' '}
                  <strong>{indicators.frd_required.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€</strong> à votre trésorerie actuelle.
                </p>
              )}
              {indicators.frd_required === 0 && (
                <p className="text-sm mt-3 text-green-800 bg-green-100 p-3 rounded">
                  Votre trésorerie actuelle est suffisante pour couvrir tous les flux de votre plan de trésorerie.
                  Votre solde restera positif sur toute la période.
                </p>
              )}
            </div>
          </div>

          {indicators.rupture_date && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle size={24} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-2">Date de Rupture de Trésorerie</h3>
                  <p className="text-red-700">
                    Dans le scénario stress test, votre trésorerie atteindrait zéro le{' '}
                    <strong>{new Date(indicators.rupture_date).toLocaleDateString('fr-FR')}</strong>
                  </p>
                </div>
              </div>
            </div>
          )}

          {indicators.safety_gap < 0 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign size={24} className="text-blue-600" />
                Recommandations Actionnables
              </h3>
              <div className="space-y-4">
                <div className="border-l-4 border-blue-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900 mb-1">Option 1 : Injection de Cash</h4>
                  <p className="text-gray-700">
                    Lever <strong>{indicators.recommended_cash_injection.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€</strong>
                    {' '}pour atteindre un niveau de sécurité acceptable
                  </p>
                </div>

                <div className="border-l-4 border-orange-500 pl-4 py-2">
                  <h4 className="font-semibold text-gray-900 mb-1">Option 2 : Réduction des Coûts</h4>
                  <p className="text-gray-700">
                    Réduire les charges de <strong>{indicators.recommended_monthly_cost_reduction.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€/mois</strong>
                  </p>
                </div>

                {indicators.recommended_receivable_acceleration_days > 0 && (
                  <div className="border-l-4 border-green-500 pl-4 py-2">
                    <h4 className="font-semibold text-gray-900 mb-1">Option 3 : Accélération des Encaissements</h4>
                    <p className="text-gray-700">
                      Accélérer les encaissements de <strong>{indicators.recommended_receivable_acceleration_days} jours</strong>
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {indicators.surplus_state && indicators.surplus_state !== 'deficit' && indicators.surplus_state !== 'balanced' && indicators.burn_rate_monthly > 0 && (
            <div className="bg-white rounded-lg shadow p-6 mb-6">
              <div className="flex items-start gap-4 mb-6">
                <div className="flex-shrink-0 p-3 bg-teal-100 rounded-lg">
                  <TrendingUp size={28} className="text-teal-600" />
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 flex items-center gap-2">
                    Excédent de trésorerie
                  </h3>
                  <div className="text-gray-700 space-y-2">
                    <p>
                      À votre rythme actuel, votre trésorerie couvre{' '}
                      <strong className="text-teal-700">{indicators.months_covered?.toFixed(1) || '0'} mois de charges</strong>.
                    </p>
                    <p>
                      Après sécurisation de{' '}
                      <strong>{(indicators.frd_required / indicators.burn_rate_monthly).toFixed(1)} mois</strong> +{' '}
                      <strong>{indicators.security_months_configured || 0} mois de marge</strong>, il reste{' '}
                      <strong className="text-teal-700">{(indicators.mobilizable_surplus || 0).toLocaleString('fr-FR', { maximumFractionDigits: 0 })}€</strong> de trésorerie excédentaire.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                  <Lightbulb size={20} className="text-yellow-600" />
                  Pistes à explorer
                </h4>

                {indicators.surplus_recommendations && indicators.surplus_recommendations.map((rec, idx) => {
                  const iconMap = {
                    securisation: Shield,
                    optimisation: Target,
                    investissement: TrendingUp,
                    redistribution: Users,
                  };
                  const stylesMap = {
                    securisation: {
                      border: 'border-blue-500',
                      bg: 'bg-blue-50',
                      text: 'text-blue-600',
                      icon: Shield
                    },
                    optimisation: {
                      border: 'border-teal-500',
                      bg: 'bg-teal-50',
                      text: 'text-teal-600',
                      icon: Target
                    },
                    investissement: {
                      border: 'border-green-500',
                      bg: 'bg-green-50',
                      text: 'text-green-600',
                      icon: TrendingUp
                    },
                    redistribution: {
                      border: 'border-purple-500',
                      bg: 'bg-purple-50',
                      text: 'text-purple-600',
                      icon: Users
                    },
                  };
                  const Icon = iconMap[rec.category];
                  const styles = stylesMap[rec.category];

                  return (
                    <div key={idx} className={`border-l-4 ${styles.border} ${styles.bg} pl-4 pr-4 py-3 rounded-r-lg`}>
                      <div className="flex items-start gap-3">
                        <Icon size={20} className={`${styles.text} flex-shrink-0 mt-0.5`} />
                        <div className="flex-1">
                          <h5 className="font-semibold text-gray-900 mb-1">{rec.title}</h5>
                          <p className="text-sm text-gray-700 mb-3">{rec.message}</p>
                          <div className="space-y-1.5">
                            <p className="text-xs font-medium text-gray-600 uppercase tracking-wide">Vous pouvez :</p>
                            <ul className="space-y-1">
                              {rec.actions.map((action, actionIdx) => (
                                <li key={actionIdx} className="text-sm text-gray-700 flex items-start gap-2">
                                  <span className={`${styles.text} font-bold mt-0.5`}>•</span>
                                  <span>{action}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-start gap-2">
                  <Wallet size={18} className="text-gray-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-gray-700">
                    <p className="font-medium mb-1">TrezoOne met en lumière des choix</p>
                    <p className="text-gray-600">
                      Ces pistes sont données à titre informatif pour vous aider à prendre des décisions éclairées.
                      TrezoOne n'agit pas et ne prescrit aucun conseil financier. Les choix vous appartiennent.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Détails de la Simulation</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-500">Trésorerie actuelle</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.opening_cash.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Trésorerie minimale atteinte sur le plan</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.min_cash_over_horizon.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Encaissements moyens mensuels prévus</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.avg_cash_in_monthly.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Burn rate mensuel (total)</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.burn_rate_monthly.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Burn rate mensuel (récurrent)</p>
                <p className="text-lg font-semibold text-green-600">
                  {indicators.burn_rate_monthly_recurring.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Burn rate journalier moyen</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.burn_rate_daily.toLocaleString('fr-FR', { minimumFractionDigits: 0 })}€
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">TSF avec burn rate total</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.tsf_months > 100 ? '∞' : `${indicators.tsf_months.toFixed(1)} mois`}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">TSF avec burn rate récurrent</p>
                <p className="text-lg font-semibold text-green-600">
                  {indicators.tsf_months_recurring > 100 ? '∞' : `${indicators.tsf_months_recurring.toFixed(1)} mois`}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Jours avant rupture (plan actuel)</p>
                <p className="text-lg font-semibold text-gray-900">
                  {indicators.frd_safety_days} jours
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Dépenses exceptionnelles détectées</p>
                <p className="text-lg font-semibold text-orange-600">
                  {indicators.exceptional_expenses?.length || 0}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Horizon de simulation</p>
                <p className="text-lg font-semibold text-gray-900">
                  180 jours (6 mois)
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {stressTests.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mt-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Historique des Stress Tests</h3>
          <div className="space-y-2">
            {stressTests.map((test) => (
              <div
                key={test.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors cursor-pointer"
                onClick={() => setSelectedTest(test.id)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <h4 className="font-semibold text-gray-900">{test.test_name}</h4>
                    <p className="text-sm text-gray-500">
                      {new Date(test.created_at).toLocaleDateString('fr-FR')} - {test.horizon_days} jours
                    </p>
                  </div>
                  <span className="text-sm text-gray-500">{test.test_type}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
